import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { orderBy } from 'natural-orderby';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import TimeSeriesComposedBarLineGraph from './TimeSeriesComposedBarLineGraph';
import {
    formatTimeSeriesDatasetMultiMetric,
    formatTimeSeriesDataset,
} from '../../utils/timeSeriesDataFormatUtils';
import {
    hasMetricSources,
    getMetricSourceForRequest,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import {
    getUniqueArray,
    getTimeSeriesTickFormatter,
    getFormattedTimeFromMilliseconds,
} from '../../utils/dataFormatUtils';

/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics, configuration }) {
    const barRequest = metricRequests.find(
        (request) => request.id === configuration.barMetricRequestId
    );

    const lineRequest = metricRequests.find(
        (request) => request.id === configuration.lineMetricRequestId
    );

    if (
        !barRequest ||
        barRequest.metrics.length === 0 ||
        !lineRequest ||
        lineRequest.metrics.length === 0
    ) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const barMetricSource = getMetricSourceForRequest(metrics, barRequest);
    const lineMetricSource = getMetricSourceForRequest(metrics, lineRequest);
    if (!barMetricSource || !lineMetricSource) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized([barMetricSource, lineMetricSource])) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources([barMetricSource, lineMetricSource], false)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [barMetricSource, lineMetricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { barRequest, lineRequest, barMetricSource, lineMetricSource };
}
/* eslint-enable complexity */

function processMultipleMetrics(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    disconnectedGraph
) {
    const dataLabels = metricRequest.metrics;
    const formattedData = formatTimeSeriesDatasetMultiMetric(
        metricSource.bucketTimes,
        metricSource.metricSeries,
        metricRequest.metrics,
        numberDecimalFormatter,
        disconnectedGraph,
        true
    );

    return [dataLabels, formattedData];
}

function processSingleMetric(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    disconnectedGraph,
    suffix
) {
    const metricValueKey = `${metricRequest.metrics[0]} ${suffix}`;
    const metricNameKey =
        metricRequest.groupBy.length === 0 ? null : metricRequest.groupBy[0];

    // prettier-ignore
    const dataLabels = !metricNameKey
        ? [metricValueKey]
        : orderBy(getUniqueArray(metricSource.metricSeries.map(
            metric => metric[metricNameKey]
        )));

    const formattedData = formatTimeSeriesDataset(
        metricSource.bucketTimes,
        metricSource.metricSeries,
        dataLabels,
        metricNameKey,
        metricValueKey,
        numberDecimalFormatter,
        disconnectedGraph,
        true
    );

    return [dataLabels, formattedData];
}

function processMetricRequest(
    metricRequest,
    metricSource,
    numberDecimalFormatter,
    disconnectedGraph,
    suffix
) {
    // prettier-ignore
    return metricRequest.metrics.length > 1
        ? processMultipleMetrics(
            metricRequest,
            metricSource,
            numberDecimalFormatter,
            disconnectedGraph
        )
        : processSingleMetric(
            metricRequest,
            metricSource,
            numberDecimalFormatter,
            disconnectedGraph,
            suffix
        );
}

// eslint-disable-next-line complexity
export default function TimeSeriesComposedBarLineGraphWrapper(props) {
    const {
        barRequest,
        lineRequest,
        barMetricSource,
        lineMetricSource,
        metricSourceErrors,
        errorKey,
    } = getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const [barDataLabels, formattedBarData] = processMetricRequest(
        barRequest,
        barMetricSource,
        props.numberDecimalFormatter,
        props.configuration.disconnectedGraph,
        '(left)'
    );

    const [lineDataLabels, formattedLineData] = processMetricRequest(
        lineRequest,
        lineMetricSource,
        props.numberDecimalFormatter,
        props.configuration.disconnectedGraph,
        '(right)'
    );

    const formattedData = _(formattedBarData)
        .merge(formattedLineData)
        .values()
        .value();

    const dataFormatter = props.configuration.isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    return (
        <>
            {barMetricSource.isTruncated && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${barRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container">
                <TimeSeriesComposedBarLineGraph
                    {...props}
                    bucketTimes={barMetricSource.bucketTimes}
                    chartProperties={props.configuration}
                    dataPoints={formattedData}
                    dateFormatter={getTimeSeriesTickFormatter(
                        barRequest.timeAndPrecision.precisionType
                    )}
                    barDataLabels={barDataLabels}
                    dataFormatter={dataFormatter}
                    lineDataLabels={lineDataLabels}
                />
            </div>
        </>
    );
}

TimeSeriesComposedBarLineGraphWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
};
